import { selectIsBot } from '@/redux/meta/selector'
import React from 'react'
import { useSelector } from 'react-redux'

interface UseLazyCategoryRenderProps {
  menu?: Oms.MenuCategory[]
}

interface UseLazyCategoryRender {
  shouldShowCategoryDetails: (categoryId: string) => boolean
  shouldShowCategorySkeleton: (categoryId: string) => boolean
  showCategorySkeleton: (categoryId: string) => void
  showCategoryDetails: (categoryId: string) => void
  registerCategoryInViewport: (categoryId: string) => void
  unregisterCategoryFromViewport: (categoryId: string) => void
}

const useLazyCategoryRender = ({
  menu,
}: UseLazyCategoryRenderProps): UseLazyCategoryRender => {
  const disabled = useSelector(selectIsBot)
  const [showSkeleton, setShowSkeleton] = React.useState<
    Record<string, boolean>
  >({})
  const [showDetails, setShowDetails] = React.useState<Record<string, boolean>>(
    {}
  )
  const initializedRef = React.useRef(false)
  const viewportTimersRef = React.useRef<Record<string, NodeJS.Timeout>>({})

  React.useEffect(() => {
    if (!menu || initializedRef.current) return

    let details: Record<string, boolean>
    if (disabled) {
      // Show all categories if disabled
      details = Object.fromEntries(menu.map((cat) => [cat.id, true]))
    } else {
      // Show all empty categories up to and including the first non-empty category
      const firstNonEmptyIdx = menu.findIndex(
        (cat) => cat.items && cat.items.length > 0
      )
      const lastIdx =
        firstNonEmptyIdx === -1 ? menu.length - 1 : firstNonEmptyIdx
      details = Object.fromEntries(
        menu.map((cat, idx) => [cat.id, idx <= lastIdx])
      )
    }

    setShowDetails(details)
    initializedRef.current = true
  }, [menu, disabled])

  const displayedCategories = React.useMemo(() => {
    return menu?.reduce((acc, category, idx) => {
      if (showDetails?.[category.id] || showSkeleton[category.id]) {
        acc[category.id] = true
      }

      if (showDetails?.[category.id]) {
        const nextCategory = menu[idx + 1]
        if (nextCategory && !showDetails?.[nextCategory.id]) {
          acc[nextCategory.id] = true
        }
      }

      return acc
    }, {} as Record<string, boolean>)
  }, [showSkeleton, showDetails, menu])

  const shouldShowCategorySkeleton = (catId: string) =>
    Boolean(displayedCategories?.[catId])
  const shouldShowCategoryDetails = (catId: string) =>
    Boolean(showDetails?.[catId])

  const showCategorySkeleton = React.useCallback(
    (categoryId: string) => {
      setShowSkeleton((state) => ({ ...state, [categoryId]: true }))
      const index = menu?.findIndex(({ id }) => id === categoryId)
      if (!index || index <= 1) return
      const prevOneCat = menu?.[index - 1]?.id
      const prevTwoCat = menu?.[index - 2]?.id
      // NOTE: Show details of 2 previous categories to prevent scroll position to jump unexpectedly in case previous category skeletons is in viewport.
      setShowDetails((state) => ({
        ...state,
        ...(prevOneCat
          ? {
              [prevOneCat]: true,
            }
          : {}),
        ...(prevTwoCat
          ? {
              [prevTwoCat]: true,
            }
          : {}),
      }))
    },
    [menu]
  )

  const showCategoryDetails = (categoryId: string) => {
    // Fake network request
    setTimeout(() => {
      setShowDetails((state) => ({
        ...state,
        [categoryId]: true,
      }))
    }, 150)
  }

  // Register category when it enters viewport
  const registerCategoryInViewport = React.useCallback((categoryId: string) => {
    // Clear any existing timer for this category
    if (viewportTimersRef.current[categoryId]) {
      clearTimeout(viewportTimersRef.current[categoryId])
    }

    // Set 500ms delay before showing details
    viewportTimersRef.current[categoryId] = setTimeout(() => {
      showCategoryDetails(categoryId)
      delete viewportTimersRef.current[categoryId]
    }, 500)
  }, [])

  // Unregister category when it leaves viewport
  const unregisterCategoryFromViewport = React.useCallback(
    (categoryId: string) => {
      // Clear timer if category leaves viewport before 500ms
      if (viewportTimersRef.current[categoryId]) {
        clearTimeout(viewportTimersRef.current[categoryId])
        delete viewportTimersRef.current[categoryId]
      }
    },
    []
  )

  // Cleanup effect
  React.useEffect(() => {
    return () => {
      // Clear all timers on unmount
      Object.values(viewportTimersRef.current).forEach(clearTimeout)
      viewportTimersRef.current = {}
    }
  }, [])

  return {
    shouldShowCategoryDetails,
    shouldShowCategorySkeleton,
    showCategorySkeleton,
    showCategoryDetails,
    registerCategoryInViewport,
    unregisterCategoryFromViewport,
  }
}

export default useLazyCategoryRender
